import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import _ from 'lodash'
import { observer } from 'mobx-react'

// Components
import { Button } from '../../components/Button'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'
import { DataTable } from '../../components/DataTable'
import { renderSelectEditor } from '../../components/CustomEditor'
import { Toggle } from '../../components/Toggle'
import RulePanel from './RulesPanel'

// Services, Utils & Hooks
import { getClient } from '../../services/clients.service'
import { getClientComposites } from '../../services/composites.service'
import {
  createClientRule,
  deleteClientRules,
  getClientRules,
  updateClientRule,
} from '../../services/rules.service'
import { toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

import { DEFAULT_COLUMNS, EXTRA_COLUMNS } from './constants'

// Store
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

const ClientRules = observer(() => {
  // Context
  const { clientId } = useParams()
  const { canModifyData, setClient } = useContext(ClientDashboardStoreContext)
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const newRule = searchParams.get('new')
  const composite = searchParams.get('composite')

  // State
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [composites, setComposites] = useState([])
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [data, setData] = useState([])
  const [editRule, setEditRule] = useState(
    newRule && composite ? { type: 'override', row: { composite: { id: composite } } } : null,
  )
  const [showAllColumns, setShowAllColumns] = useState(false)

  // Pagination
  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage, totalRecords } = pagination

  // Sorting
  const { sorting } = useSorting('periodFrom')
  const { sortedColumn } = sorting

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  useEffect(() => {
    const getClientAndComposites = async () => {
      setLoading(true)

      const [updatedClient, updatedComposites] = await Promise.all([
        getClient(clientId, setError, () => {}),
        getClientComposites(`/clients/${clientId}/composites?limit=99999`, setError, () => {}),
      ])

      if (updatedClient) {
        setClient(updatedClient)
      }

      if (updatedComposites)
        setComposites(_.map(updatedComposites.results, (c) => ({ label: c.name, value: c.id })))
      setLoading(false)
    }

    getClientAndComposites()
  }, [])

  const getUpdatedRules = async () => {
    const response = await getClientRules(
      `/clients/${clientId}/rules?expand=composite&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}`,
      handleErrors,
      setLoading,
    )

    if (response) {
      setData(response.results)
      setTotalRecords(response.count)
    }
  }

  useEffect(() => {
    getUpdatedRules()
  }, [perPage, currentPage, sortedColumn])

  const renderEditButton = (row) => (
    <Button
      background="bg-blue"
      label="Edit"
      onClick={() => {
        if (composites) {
          setEditRule({ type: 'override', row })
        } else {
          setEditRule({ type: 'rule', row })
        }
      }}
    />
  )

  const EDIT_RULE = {
    header: '',
    frozen: true,
    alignFrozen: 'right',
    body: renderEditButton,
    style: { minWidth: '80px' },
  }

  const COLUMNS = useMemo(() => {
    const columns = [...DEFAULT_COLUMNS]

    if (showAllColumns) {
      columns.push(
        ...EXTRA_COLUMNS.map((column) => ({
          ...column,
          header: column.header,
          body: column.body,
          editor: column.editor,
          style: { minWidth: '200px' },
        })),
      )
    }

    if (composites) {
      columns.unshift({
        field: 'composite.id',
        frozen: true,
        header: 'Composite',
        body: (row) => row.composite.name,
        editor: (options) =>
          renderSelectEditor(
            composites,
            options.rowData.composite.id,
            'Select an Option',
            options,
          ),
        style: { minWidth: '200px' },
      })
    }

    columns.push(EDIT_RULE)

    return columns
  }, [showAllColumns, composites])

  return (
    <PageContainer>
      <StateContainer error={error} loading={loading}>
        <div className="flex size-full flex-col gap-4 overflow-y-auto bg-background">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <h3 className="text-xl font-semibold leading-6 text-gray-900">Rules</h3>

              <Toggle
                label="Show All Columns"
                onChange={() => setShowAllColumns(!showAllColumns)}
                checked={showAllColumns}
              />
            </div>

            {!loading && (totalRecords > 0 || composites) && (
              <Button
                background="bg-blue"
                disabled={!canModifyData}
                label="Add Rule"
                onClick={() => {
                  setEditRule({ type: composites ? 'override' : 'rule', row: null })
                }}
              />
            )}
          </div>

          {/* TODO: Determine if we still need this */}
          {/* {!loading && totalRecords === 0 && globalRules && (
            <Button
              background="bg-green"
              disabled={!editable}
              label="Import from Global Rule Default"
              onClick={() => {
                const clientRules = { ...globalRules, periodFrom: null, periodTo: null }
                delete clientRules.id
                delete clientRules.createdAt
                delete clientRules.modifiedAt

                createClientRule(clientId, clientRules, handleErrors, setLoading, async () => {
                  handleSuccess('Rules imported successfully.')
                  getUpdatedData()

                  const updatedClient = await getClient(
                    clientId,
                    () => {},
                    () => {},
                  )
                  if (updatedClient) setClient(updatedClient)
                })
              }}
            />
          )} */}

          <DataTable
            data={data}
            columns={COLUMNS}
            loadingDelete={loadingDelete}
            hoverEffect={false}
            loading={loading}
            pagination={pagination}
            sorting={sorting}
            onRowDeleteConfirm={(row) => {
              deleteClientRules(clientId, row.id, handleErrors, setLoadingDelete, () => {
                handleSuccess('Rule deleted.')
                getUpdatedRules()
              })
            }}
            areRowsDeleteable
            rowDeleteConfirm={() => ({
              title: 'Confirm Delete',
              type: 'warning',
              message: (
                <div className="flex flex-col">
                  <span className="">Are you sure you want to delete this rule?</span>
                </div>
              ),
            })}
          />

          <RulePanel
            clientId={clientId}
            closePanel={() => {
              setEditRule(null)
              getUpdatedRules()

              // Clear search params
              navigate(`/clients/${clientId}/settings/rules`)
            }}
            composites={composites}
            panelOpen={!!editRule}
            rule={editRule?.row}
            addRule={createClientRule}
            updateRule={updateClientRule}
          />
        </div>
      </StateContainer>
    </PageContainer>
  )
})

export default ClientRules
